import { createContext, useState, useEffect, useContext, PropsWithChildren } from "react";
import jwt_decode from "jwt-decode";
import useLocalStorage from "~hooks/useLocalStorage";
import { authFetch } from "~helpers/authFetch";
import { useNavigate } from "react-router-dom";
import { ROLES } from "~routes/constants";

interface IPermission {
  id: number;
  id_stream: number;
  screen: string;
  stream?: {
    id_stream: number;
    stream_name: string;
    brand: string;
    sales_channel: number;
  };
}

interface IUser {
  brand: string;
  exp: Date;
  iat: Date;
  id_stream: number;
  id_user_somalive?: number;
  role: ROLES;
  segredo: boolean;
  stream: string;
  permissions?: IPermission[];
}

interface AuthContextData {
  signed: boolean;
  user: IUser | null;
  loginError: boolean;
  permissions: IPermission[];
  permissionsLoading: boolean;
  permissionsError: boolean;
  reloadPermissions: () => Promise<void>;
  hasPermission: (streamId: number, screen: string) => boolean;
  getAvailableStreams: () => Array<{
    id_stream: number;
    stream_name: string;
    brand: string;
  }>;
  Login(user: object): Promise<void>;
  ValidateUserToken(): Promise<void>;
  Logout(): void;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate();
  const [appToken, setAppToken] = useLocalStorage<string | null>("@app:token", null);
  const [user, setUser] = useState<IUser | null>(() => {
    try {
      return appToken ? jwt_decode<IUser>(appToken) : null;
    } catch {
      return null;
    }
  });
  const [loginError, setloginError] = useState<boolean>(false);
  const [permissions, setPermissions] = useState<IPermission[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(() =>
    Boolean(appToken)
  );
  const [permissionsError, setPermissionsError] = useState<boolean>(false);

  useEffect(() => {
    if (!appToken) {
      setPermissions([]);
      setPermissionsLoading(false);
      setPermissionsError(false);
      return;
    }

    let decodedUser: IUser;
    try {
      decodedUser = jwt_decode<IUser>(appToken);
    } catch {
      setUser(null);
      setPermissions([]);
      setPermissionsLoading(false);
      return;
    }

    setUser(decodedUser);

    if (decodedUser.id_user_somalive) {
      setPermissionsLoading(true);
      setPermissionsError(false);
      loadUserPermissions(decodedUser).catch((error) => {
        console.error("Error loading permissions on mount:", error);
      });
    } else {
      setPermissions([]);
      setPermissionsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appToken]);

  const loadUserPermissions = async (userToValidate?: IUser) => {
    try {
      setPermissionsError(false);
      const response = await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/users/me/permissions`,
      });
      const data = await response.json();
      const loadedPermissions = data.permissions || [];
      setPermissions(loadedPermissions);

      const userForValidation = userToValidate || user;
      if (userForValidation) {
        await validateAndAdjustStream(loadedPermissions, userForValidation);
      }
    } catch (error) {
      console.error("Error loading user permissions:", error);
      setPermissions([]);
      setPermissionsError(true);
    } finally {
      setPermissionsLoading(false);
    }
  };

  const reloadPermissions = async () => {
    if (!user?.id_user_somalive) return;
    setPermissionsLoading(true);
    await loadUserPermissions(user);
  };

  const validateAndAdjustStream = async (
    loadedPermissions: IPermission[],
    userToValidate: IUser
  ) => {
    if (!userToValidate) return;

    // Admin sempre tem acesso a todas as streams
    if (userToValidate.role === ROLES.admin) {
      // Admin pode manter qualquer stream, não precisa validar
      return;
    }

    // Obter streams disponíveis do usuário (únicos por id_stream)
    const availableStreamIds = [
      ...new Set(
        loadedPermissions.map((perm) => perm.id_stream).filter((id): id is number => !!id)
      ),
    ];

    if (availableStreamIds.length === 0) {
      console.warn("User has no stream permissions, logging out");
      Logout();
      return;
    }

    const currentStreamStorage = window.localStorage.getItem("current_stream");
    if (currentStreamStorage) {
      try {
        const stream = JSON.parse(currentStreamStorage);
        // Verificar se o usuário ainda tem acesso à stream salva
        const hasAccessToStoredStream = availableStreamIds.includes(stream.id);
        if (!hasAccessToStoredStream) {
          // Se não tem mais acesso, limpar
          console.log(
            "=== Clearing current_stream storage - user no longer has access ===",
            {
              storedStreamId: stream.id,
              availableStreamIds,
            }
          );
          window.localStorage.removeItem("current_stream");
        }
      } catch (e) {
        // Ignorar erro de parse
        console.error("Error parsing current_stream:", e);
      }
    }
  };

  const hasPermission = (streamId: number, screen: string): boolean => {
    if (!user) return false;
    // Admin tem acesso total
    if (user.role === ROLES.admin) return true;

    // Home é sempre permitida para qualquer usuário logado
    if (screen === "home") {
      return true;
    }

    // Verificar se tem permissão específica para esta stream e screen
    return permissions.some(
      (perm) => perm.id_stream === streamId && perm.screen === screen
    );
  };

  const getAvailableStreams = (): Array<{
    id_stream: number;
    stream_name: string;
    brand: string;
  }> => {
    if (!user) return [];

    // Admin tem acesso a todas as streams (será filtrado pelo BrandSelector)
    if (user.role === ROLES.admin) {
      return [];
    }

    // Retornar streams únicas das permissões
    const streamMap = new Map<
      number,
      { id_stream: number; stream_name: string; brand: string }
    >();

    permissions.forEach((perm) => {
      if (perm.id_stream && perm.stream) {
        if (!streamMap.has(perm.id_stream)) {
          streamMap.set(perm.id_stream, {
            id_stream: perm.id_stream,
            stream_name: perm.stream.stream_name,
            brand: perm.stream.brand,
          });
        }
      }
    });

    return Array.from(streamMap.values());
  };

  const Logout = () => {
    setUser(null);
    setAppToken(null);
    setloginError(false);
    setPermissions([]);
    setPermissionsLoading(false);
    setPermissionsError(false);
    // Limpar current_stream do localStorage ao fazer logout
    window.localStorage.removeItem("current_stream");
    navigate("/");
  };

  async function Login(userData: object) {
    const requestOptions = {
      method: "POST",
      body: JSON.stringify(userData),
      headers: { "Content-Type": "application/json" },
    };

    await fetch(`${process.env.REACT_APP_BACKEND_URL}/auth/login`, requestOptions)
      .then((response) => {
        if (response.status !== 200) {
          setloginError(true);
          throw new Error("Login Falhou");
        } else {
          return response.text();
        }
      })
      .then(async (result) => {
        setloginError(false);
        // Limpar current_stream do localStorage no momento do login
        window.localStorage.removeItem("current_stream");
        setAppToken(result);
        try {
          const decodedUser = jwt_decode<IUser>(result);
          setUser(decodedUser);
          // Buscar permissões após login bem-sucedido
          // Passar decodedUser para validação imediata
          if (decodedUser.id_user_somalive) {
            await loadUserPermissions(decodedUser);
          }
          // Redirecionar após login bem-sucedido
          if (decodedUser.role === ROLES.admin || decodedUser.role === ROLES.usuario) {
            navigate("/home");
          } else if (decodedUser.role === ROLES.chat) {
            navigate("/chat");
          }
        } catch {
          throw new Error("Token inválido");
        }
      })
      .catch(() => {
        setloginError(true);
        throw new Error("Login Falhou");
      });
  }

  async function ValidateUserToken() {
    try {
      await authFetch({
        path: `${process.env.REACT_APP_BACKEND_URL}/auth/checkToken`,
      });
    } catch (error) {
      Logout();
      throw new Error("Problemas na autenticação");
    }
  }

  return (
    <AuthContext.Provider
      value={{
        signed: Boolean(user),
        user,
        loginError,
        permissions,
        permissionsLoading,
        permissionsError,
        reloadPermissions,
        hasPermission,
        getAvailableStreams,
        Login,
        Logout,
        ValidateUserToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);

  return context;
}
